import React, { useEffect, useState, useRef } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { BlogPost, getBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost } from '../../services/blogService';
import { uploadFileToS3 } from '../../services/s3Upload';

const AdminBlogManager: React.FC = () => {
  const { showError, showSuccess } = useNotification();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [form, setForm] = useState({
    title: '',
    imageUrl: '',
    linkUrl: '',
    order: 0,
    isActive: true,
  });

  const isFormValid = !!form.title.trim() && !!form.linkUrl.trim();

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await getBlogPosts();
      setPosts(data);
    } catch (e) {
      console.error(e);
      showError('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ title: '', imageUrl: '', linkUrl: '', order: posts.length, isActive: true });
    setShowModal(true);
  };

  const openEdit = (p: BlogPost) => {
    setEditing(p);
    setForm({ title: p.title, imageUrl: p.imageUrl || '', linkUrl: p.linkUrl, order: p.order || 0, isActive: p.isActive !== false });
    setShowModal(true);
  };

  const submit = async () => {
    try {
      if (!form.title.trim() || !form.linkUrl.trim()) {
        showError('Please fill title and link URL');
        return;
      }
      if (isUploadingImage) {
        showError('Please wait for the image upload to finish');
        return;
      }
      setIsSubmitting(true);
      if (editing) {
        await updateBlogPost(editing.id, { ...form });
        showSuccess('Blog updated');
      } else {
        await createBlogPost({ ...form } as any);
        showSuccess('Blog created');
      }
      setShowModal(false);
      await load();
    } catch (e) {
      console.error(e);
      showError('Failed to save');
    } finally {
      setIsSubmitting(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await deleteBlogPost(id);
      showSuccess('Deleted');
      await load();
    } catch (e) {
      console.error(e);
      showError('Failed to delete');
    }
  };

  const toggleActive = async (p: BlogPost) => {
    try {
      await updateBlogPost(p.id, { isActive: !p.isActive });
      await load();
    } catch (e) {
      showError('Failed to update status');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Blog Manager</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Manage blog cards shown on the Discover page (image + title + link)</p>
        </div>
        <button onClick={openAdd} className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg">Add Blog</button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500 dark:text-gray-400">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map(p => (
            <div key={p.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="h-40 bg-gray-100 dark:bg-gray-700 relative">
                {p.imageUrl ? (
                  <img 
                    src={p.imageUrl} 
                    alt={p.title} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div class="flex items-center justify-center h-full text-red-500 text-xs p-2">Image failed to load</div>';
                      }
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-xs text-gray-500 dark:text-gray-400">
                    No image
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2">{p.title}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">{p.linkUrl}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${p.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{p.isActive ? 'Active' : 'Inactive'}</span>
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => openEdit(p)} className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded">Edit</button>
                  <button onClick={() => toggleActive(p)} className="px-3 py-2 text-sm rounded {p.isActive ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}">{p.isActive ? 'Deactivate' : 'Activate'}</button>
                  <button onClick={() => remove(p.id)} className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{editing ? 'Edit Blog' : 'Add Blog'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Title *</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Image URL (optional)</label>
                <input 
                  value={form.imageUrl} 
                  onChange={e => setForm({ ...form, imageUrl: e.target.value })} 
                  placeholder="https://images.unsplash.com/photo-..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" 
                />
                <div className="mt-2 flex items-center gap-2">
                  <label className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded cursor-pointer">
                    <input 
                      ref={fileInputRef}
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={async (e) => {
                        const file = e.target.files && e.target.files[0];
                        if (!file) return;
                        
                        // Validate file type
                        if (!file.type.startsWith('image/')) {
                          showError('Please select an image file');
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                          return;
                        }
                        
                        // Validate file size (10MB limit)
                        if (file.size > 10 * 1024 * 1024) {
                          showError('Image size must be less than 10MB');
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                          return;
                        }
                        
                        try {
                          setIsUploadingImage(true);
                          setUploadProgress(0);
                          console.log('Starting image upload...', { fileName: file.name, fileSize: file.size, fileType: file.type });
                          const result = await uploadFileToS3(file, { 
                            makePublic: true, 
                            keyPrefix: 'blog',
                            onProgress: (p) => setUploadProgress(p)
                          });
                          console.log('Upload result:', result);
                          
                          if (!result || !result.publicUrl) {
                            throw new Error('Upload succeeded but no public URL returned');
                          }
                          
                          setForm({ ...form, imageUrl: result.publicUrl });
                          console.log('Image URL set to:', result.publicUrl);
                          showSuccess('Image uploaded successfully');
                        } catch (err: any) {
                          console.error('Upload error details:', err);
                          const errorMessage = err?.message || err?.code || 'Upload failed. Please check your browser console for details.';
                          showError(errorMessage);
                        } finally {
                          // Safely clear the input value using the ref
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                          setIsUploadingImage(false);
                          setUploadProgress(null);
                        }
                      }} 
                    />
                    Upload image
                  </label>
                  {isUploadingImage && (
                    <span className="text-xs text-blue-600 dark:text-blue-400">Uploading image{typeof uploadProgress === 'number' ? ` ${uploadProgress}%` : '...'}
                    </span>
                  )}
                  {!isUploadingImage && form.imageUrl && (
                    <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">✓ Image uploaded</span>
                  )}
                  {!isUploadingImage && !form.imageUrl && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">or paste a direct image URL above</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use direct image URL. For Unsplash: Right-click image → "Copy image address" or use format: https://images.unsplash.com/photo-...
                </p>
                {form.imageUrl && (
                  <div className="mt-2 h-32 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                    <img 
                      src={form.imageUrl} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = '<div class="flex items-center justify-center h-full text-red-500 text-xs">Image failed to load. Check URL.</div>';
                        }
                      }}
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Link URL (Wix) *</label>
                <input value={form.linkUrl} onChange={e => setForm({ ...form, linkUrl: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Order</label>
                  <input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value || '0', 10) })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"><input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded">Cancel</button>
              <button 
                onClick={submit} 
                disabled={!isFormValid || isUploadingImage || isSubmitting}
                title={!isFormValid ? 'Fill all required fields' : isUploadingImage ? 'Wait for upload to finish' : ''}
                className={`px-4 py-2 rounded ${(!isFormValid || isUploadingImage || isSubmitting) ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-black dark:bg-white text-white dark:text-black'}`}
              >
                {isSubmitting ? (editing ? 'Updating...' : 'Adding...') : (editing ? 'Update' : 'Add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlogManager;
